import type { WalkResult, WalkNode } from "@archlang/engine";

export function findEnclosingRange(
  walk: WalkResult,
  stepIndex: number | null,
): { startIndex: number; endIndex: number; label: string } | null {
  if (stepIndex === null) return null;
  for (const [groupId, range] of walk.collapsedGroups) {
    if (stepIndex >= range.startIndex && stepIndex <= range.endIndex) {
      const name = findDoTargetName(walk.tree, groupId) ?? "subprocess";
      return { startIndex: range.startIndex, endIndex: range.endIndex, label: name };
    }
  }
  for (const [, range] of walk.parallelGroups) {
    if (stepIndex >= range.startIndex && stepIndex <= range.endIndex) {
      return { startIndex: range.startIndex, endIndex: range.endIndex, label: "parallel" };
    }
  }
  return null;
}

/**
 * Locate a `do` node by its nodeId and return its target subprocess name,
 * so the scrubber label can show `7–10 · NotifyCustomer` rather than the
 * generic "subprocess" fallback.
 */
function findDoTargetName(
  nodes: readonly WalkNode[],
  nodeId: string,
): string | undefined {
  for (const n of nodes) {
    if (n.kind === "do" && n.nodeId === nodeId) return n.target;
    if (n.kind === "each") {
      const r = findDoTargetName(n.children, nodeId);
      if (r) return r;
    } else if (n.kind === "if") {
      for (const b of n.branches) {
        const r = findDoTargetName(b.children, nodeId);
        if (r) return r;
      }
      if (n.elseBranch) {
        const r = findDoTargetName(n.elseBranch.children, nodeId);
        if (r) return r;
      }
    } else if (n.kind === "switch") {
      for (const c of n.cases) {
        const r = findDoTargetName(c.children, nodeId);
        if (r) return r;
      }
    } else if (n.kind === "try") {
      const r = findDoTargetName(n.tryBranch.children, nodeId);
      if (r) return r;
      for (const c of n.catches) {
        const r2 = findDoTargetName(c.children, nodeId);
        if (r2) return r2;
      }
    } else if (n.kind === "parallel") {
      for (const b of n.branches) {
        const r = findDoTargetName(b.children, nodeId);
        if (r) return r;
      }
    } else if (n.kind === "do" && n.body) {
      const r = findDoTargetName(n.body, nodeId);
      if (r) return r;
    }
  }
  return undefined;
}
